import ElasticSearchFiltersFactory from './search/elasticSearchFiltersFactory';

/*
 * Fetches query template from s3 if it has not been fetched yet.
 */
async function _fetchTemplate(template) {
  if (template.body === '') {
    const response = await fetch(template.url,{
      headers: {
        'Content-Type': 'text/plain'
      }
    });
    return await response.text();
  } else {
    return Promise.resolve(template.body);
  }
}

/*
 * Facet names do not get prepended with 'child_items'
 */
function _mapFacetFieldName(input) {
  return ['subjects','parent_group'].includes(input) ? `${input}.enum` : input;
}

/*
 * This maps the client supplied facets into a structure supported by ElasticSearch
 */
function _mapFacets(facets) {
  if (facets == {}) { return {}; }
  //convert a value facet into elasticsearch format
  const mapValueFacet = (facet, field) => {
    const size = {
      'grades': 24,
      'parent_group.enum': 30
    }[field] || 10;

    return { terms: { field, size } };
  };

  //convert a range facet into elasticsearch format
  const mapRangeFacet = (facet, field) => {
    return { range: {
      field, ranges:
      facet[0].ranges.map(r => ({key: r.name, from: r.from, to: r.to, gte: r.gte, gt: r.gt, lte: r.lte, lt: r.lt}))
    } };
  };
  let result = {};
  //iterate and convert all facets
  for (const [key, value] of Object.entries(facets)) {
    const field = _mapFacetFieldName(key);
    if (value[0].type == 'value') {
      result[key] = mapValueFacet(value, field);
    } else if (value[0].type == 'range') {
      result[key] = mapRangeFacet(value, field);
    } else {
      throw new Error('Unknown facet type');
    }
  }
  return result;
}

/*
 * Replace placeholders in query template file
 */
function _processTemplate(template, query, page, filters) {
  //from is the record number to start result set from
  const from = Math.max(page.current - 1, 0) * page.size;
  const regex = /INSERT_QUERY_STRING/gi;
  const now = new Date().toISOString();
  const filtersFactory = new ElasticSearchFiltersFactory(filters);

  //INSERT_FROM is the numeric record index to start our return set from aka SQL offset
  //INSERT_SIZE is how many records we want in our response
  //INSERT_QUERY_STRING needs to be replaced in multiple places
  //INSERT_DATE needs to be in ISO8601 format, and for our purposes set to today
  //INSERT_FILTER_ARRAY needs to be parseable as json
  const body = template.replace('INSERT_FROM', from)
    .replace('INSERT_SIZE', page.size)
    .replace(regex, query)
    .replace('INSERT_DATE', now)
    .replace('INSERT_FILTER_ARRAY', filtersFactory.toJSON());
  return JSON.parse(body);
}

/*
 * Function to map the aggregation values from Elastic Search into a structure to
 * be used by the course search filters
 */
function _mapAggregations(facets, aggregations) {
  const mapValueAggregation = (agg) => ({ value: agg.key, count: agg.doc_count });
  const mapRangeAggregation = (agg) => (
    { name: agg.key,
      from: (agg.from_as_string || agg.from),
      to: (agg.to_as_string || agg.to),
      gte: (agg.gte_as_string || agg.gte),
      lte: (agg.lte_as_string || agg.lte),
      gt: (agg.gt_as_string || agg.gt),
      lt: (agg.lt_as_string || agg.lt),
      count: agg.doc_count
    }
  );
  let result = {};
  for (const [key, value] of Object.entries(aggregations)) {
    const facet = facets[key][0];
    if (facet.type === 'value') {
      result[key] = [{...facet, data: value.buckets.map(mapValueAggregation)}];
    } else if (facet.type === 'range') {
      result[key] = [{...facet, data: value.buckets.map(mapRangeAggregation)}];
      delete result[key][0].ranges;
    }
  }
  return result;
}

/*
 * POST payload to elasticsearch and parse result
 */
async function _query(searchUrl, body) {
  const response = await fetch(searchUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify(body) // body data type must match "Content-Type" header
  });
  return await response.json();
}

/*
 * Returns the template object from the collection based on the query
 */
function _queryTemplate(query, templates) {
  if(query === '') {
    return templates.blank;
  } else if (query.indexOf(' ') > 0) {
    return templates.phrase;
  } else {
    return templates.query;
  }
}

/*
 * Inserts required filter(s) into the filter object
 * Handles if filters.all is missing, an array, or a hash (object)
 */
function _ensureRequiredFilters(filters = {}) {
  const e_comm = {e_commerce: true};
  if (!filters.all) {
    return Object.assign({}, filters, {all: [e_comm]});
  }
  if (!Array.isArray(filters.all)) {
    //if filters.all is a hash, turn it into an array of objects based on the key/value of the hash
    filters.all = Object.entries(filters.all).map(([key, value]) => ({[key]: value}));
  }
  if (!filters.all.some(f => (Object.keys(f).includes('e_commerce')))) {
    return Object.assign({}, filters, {all: filters.all.concat(e_comm)});
  }
}

/*
 * Factory function to create the client object used for searching
 */
const makeItemSearchClient = (templates, searchUrl) => ({
  search: async (query = '', options) => {
    const {page = {}, filters = {}, min_score = 0} = options;
    const template = _queryTemplate(query, templates);
    if (template.body === '') {
      template.body = await _fetchTemplate(template);
    }
    const pageParam = Object.assign({ current: 1, size: 24 }, page);
    const _filters = _ensureRequiredFilters(filters);
    query = query.replaceAll('"','\'');
    const body = _processTemplate(template.body, query, pageParam, _filters);

    body.min_score = min_score;
    const response = await _query(searchUrl, body);
    const total_results = response.hits.total.value;
    const total_pages = Math.ceil(total_results/pageParam.size);
    const meta = { page: { ...pageParam, total_pages, total_results } };
    const rawResults = response.hits.hits.map((item) =>
      (Object.assign(item._source, { id: item._id, catalog_type: item._index})));
    return {rawInfo: {meta}, rawResults};
  },
  facets: async (query, options) => {
    const {filters = {}, facets, min_score = 0} = options;
    const pageParam = {current: 0, size: 0};
    const template = _queryTemplate('', templates);
    if (template.body === '') {
      template.body = await _fetchTemplate(template);
    }

    const _filters = _ensureRequiredFilters(filters);
    query = query.replaceAll('"','\'');
    let body = _processTemplate(template.body, query, pageParam, _filters);
    body.aggregations = _mapFacets(facets);
    body.min_score = min_score;

    const response = await _query(searchUrl, body);
    const rawResults = _mapAggregations(facets, response.aggregations);

    return {info: {facets: rawResults}};
  }
});

/*
 * Grabs topic engine if present
 */
export const topicEngine = () => {
  const courseListTopicEng = document.querySelector('.course-list__content');
  const organicNavTopicEng = document.querySelector('.mc-search-form');
  return courseListTopicEng?.dataset.topic_engine || organicNavTopicEng?.dataset.topic_engine;
};

/*
 * Object that has function to return a search client
 */
export const ItemSearch = {
  createClient: (itemEngine) => {
    const host = (itemEngine === 'items-st') ? 'vtstaging' : 'varsitytutors';
    const prefix = window.location.href.includes('localhost') ? 'https://thingproxy.freeboard.io/fetch/' : '';
    const templates = {
      query: { url:`${prefix}https://assets2.${host}.com/vt-item-search/templates/query.txt`, body: ''},
      blank: { url: `${prefix}https://assets2.${host}.com/vt-item-search/templates/blank_query.txt`, body: ''},
      phrase: { url: `${prefix}https://assets2.${host}.com/vt-item-search/templates/phrase_query.txt`, body: ''}
    };
    const catalogType = topicEngine() ? 'catalog-items,catalog-topics' : 'catalog-items';

    // Adding search_type query so that topics get ranked equal to items
    // https://www.elastic.co/guide/en/elasticsearch/reference/8.0/search-multi-search.html
    const searchUrl = `https://itemsearch.${host}.com/${catalogType}/_search?search_type=dfs_query_then_fetch`;

    return makeItemSearchClient(templates, searchUrl);
  }
};
